import React, { useEffect, useRef, useState } from 'react'
import { useIntersectionObserverLoadMore } from 'hooks/useIntersectionObserverLoadMore'
import Modal from 'common/Modal'
import Icon from 'common/Icon'
import { Loader2 } from 'lucide-react'
import { If } from '@/utility_components/ControlStatements'
import { TESTIMONIALS_DISCLAIMER_TEXT } from 'app/ProviderDetails/constants'
import { getTestimonialAttribution } from 'app/ProviderDetails/utils'
import { cn } from '@/lib/utils'

const TESTIMONIAL_MODAL_CARD_ID_PREFIX = 'testimonial-modal-'

type TestimonialsDesktopModalProps = {
  show: boolean
  onHide: () => void
  items: ProviderTestimonialItem[]
  providerDetails: Provider
  /** When set (e.g. "Read more" on a card), scroll to this testimonial; otherwise scroll to featured if any. */
  scrollToTestimonialId?: string | null
  loadMore?: () => Promise<void>
  hasMore?: boolean
  isLoading?: boolean
}

const TestimonialsDesktopModal: React.FC<TestimonialsDesktopModalProps> = ({
  show,
  onHide,
  items,
  providerDetails,
  scrollToTestimonialId = null,
  loadMore,
  hasMore = false,
  isLoading = false,
}) => {
  /** Scroll container for IO `root` + scroll-into-view; state (not ref-only) so IntersectionObserver re-subscribes when the node mounts. */
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)
  const hasAppliedInitialScrollRef = useRef(false)
  const providerFirstName = providerDetails?.firstName ?? ''

  const { sentinelRef } = useIntersectionObserverLoadMore({
    rootScroll: scrollRoot,
    active: show,
    hasMore,
    isLoading,
    loadMore,
  })

  useEffect(() => {
    if (!show) {
      hasAppliedInitialScrollRef.current = false
      return
    }

    if (
      hasAppliedInitialScrollRef.current ||
      !scrollRoot ||
      items.length === 0
    ) {
      return
    }

    if (!scrollToTestimonialId) {
      return
    }

    const elementIdForScroll = `${TESTIMONIAL_MODAL_CARD_ID_PREFIX}${scrollToTestimonialId}`
    const el = document.getElementById(elementIdForScroll)

    if (el) {
      hasAppliedInitialScrollRef.current = true
      const timer = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [show, items, scrollRoot, scrollToTestimonialId])

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      modalType="centered-content-height"
      dialogClassOverride="~max-w-[800px] ~w-full"
      contentClassOverride="~max-h-[85vh] ~flex ~flex-col"
    >
      <div className="~flex ~flex-col ~p-0 ~min-h-0">
        <header className="~relative ~shrink-0 ~pt-10 ~pr-14 ~pb-0 ~pl-8">
          <div className="~flex ~items-center ~gap-4">
            <img
              src="/static/testimonials-chat-icon.svg"
              alt=""
              className="~shrink-0"
              width={59}
              height={27}
              aria-hidden
            />
            <h2 className="~font-gibson ~text-[28px] ~font-normal ~leading-[36px] ~text-black ~m-0">
              What patients say
            </h2>
          </div>
          <button
            type="button"
            className={cn(
              '~absolute ~top-6 ~right-6 ~p-0 ~m-0 ~bg-transparent ~border-0',
              '~cursor-pointer ~leading-none',
              'hover:~opacity-80',
              '~outline-none ~focus-visible:~outline-2 ~focus-visible:~outline-sky ~focus-visible:~outline-offset-2'
            )}
            onClick={onHide}
            aria-label="Close testimonials"
          >
            <Icon icon="close" fill="black" size="24" aria-hidden />
          </button>
        </header>
        <div className="~shrink-0 ~h-px ~mt-6 ~mx-8 ~bg-grey-wash" />
        <p className="~shrink-0 ~font-gibson ~text-base ~font-normal  ~text-black ~mt-6 ~mx-8 ~mb-0">
          {TESTIMONIALS_DISCLAIMER_TEXT}
        </p>
        <div
          ref={setScrollRoot}
          className="~flex-1 ~min-h-0 ~overflow-y-auto ~mt-6 ~px-8 ~pb-8 ~pt-0 ~flex ~flex-col ~gap-0"
        >
          <div className="~flex ~flex-col ~divide-y ~divide-grey-wash">
            {items.map((item) => (
              <div
                key={item.id}
                id={`${TESTIMONIAL_MODAL_CARD_ID_PREFIX}${item.id}`}
                className="~flex ~flex-col ~py-6"
              >
                <div className="~shrink-0 ~h-10 ~w-[43px] ~mb-4">
                  <img
                    src="/static/quote-icon.svg"
                    alt=""
                    className="~block ~h-10 ~w-auto ~max-w-[43px] ~object-contain ~object-left-top"
                    width={43}
                    height={40}
                    aria-hidden
                  />
                </div>
                <div className="~flex-1">
                  <p className="~font-gibson ~text-xl ~font-normal  ~text-black ~m-0 ~whitespace-pre-wrap">
                    {item.reviewBody?.trim() ?? ''}
                  </p>
                </div>
                <p className="~font-gibson ~text-sm ~font-normal  ~text-dim-gray ~m-0 ~mt-2">
                  {getTestimonialAttribution(item, providerFirstName)}
                </p>
              </div>
            ))}
          </div>
          <If condition={hasMore}>
            <div
              ref={sentinelRef}
              className="~min-h-px ~w-full ~pointer-events-none"
            />
          </If>
          <If condition={isLoading}>
            <div className="~flex ~justify-center ~items-center ~p-4">
              <Loader2 className="~size-6 ~animate-spin" aria-hidden />
            </div>
          </If>
        </div>
      </div>
    </Modal>
  )
}

export default TestimonialsDesktopModal
